// ============================================================================
// Invoicing data layer (Phase 1.5, 2026-06-07).
// Reads use the flat-fetch + IN-lookup + client-join pattern (no PostgREST
// embeds) — the house standard for critical-path hooks.
//
// The unbilled queue reads client_billing_lines directly; that table is
// finance/exec/superuser-readable only (RLS), so non-finance roles see an
// empty queue. Invoices themselves are finance-all / AM-own / hr-hourly.
//
// Draft creation goes through create_invoice_draft_v2 (SECURITY DEFINER,
// multi-rate, contract-governed). Status transitions reuse the existing
// invoice RPCs.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type {
  ContractRateLine,
  DeptRateMapEntry,
  InvoiceLineItemRow,
  InvoiceRow,
  QueueDept,
  QueueFacility,
  ReadyToInvoiceRow,
} from '../types';
import { errorMessage } from '../../../shared/errors';
import { fetchProjectRows, type ProjectRow } from '../../project-work/lib/project-rows';

function uniq(xs: (string | null | undefined)[]): string[] {
  return Array.from(new Set(xs.filter((x): x is string => !!x)));
}

// ---------------------------------------------------------------------------
// Unbilled queue — facilities with attachable client_billing_lines
// (hours IS NOT NULL, invoice_id IS NULL), grouped by facility then dept.
// ---------------------------------------------------------------------------

interface QueueRow {
  facility_id: string | null;
  account_id: string | null;
  dept: string | null;
  hours: number | null;
  week_ending: string | null;
}

export interface InvoiceQueueResult {
  data: QueueFacility[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useInvoiceQueue(): InvoiceQueueResult {
  const [data, setData] = useState<QueueFacility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: lines, error: e1 } = await supabase
          .from('client_billing_lines')
          .select('facility_id, account_id, dept, hours, week_ending')
          .is('invoice_id', null)
          .not('hours', 'is', null);
        if (e1) throw e1;
        const rows = (lines ?? []) as QueueRow[];

        const facIds = uniq(rows.map((r) => r.facility_id));
        const [facsRes, contractsRes] = await Promise.all([
          facIds.length
            ? supabase
                .from('facilities')
                .select('facility_id, facility_name, facility_type, account_id')
                .in('facility_id', facIds)
            : Promise.resolve({ data: [], error: null }),
          facIds.length
            ? supabase
                .from('contracts')
                .select('facility_id')
                .eq('status', 'Active')
                .in('facility_id', facIds)
            : Promise.resolve({ data: [], error: null }),
        ]);
        if (facsRes.error) throw facsRes.error;
        if (contractsRes.error) throw contractsRes.error;

        const facs = (facsRes.data ?? []) as Array<{
          facility_id: string;
          facility_name: string | null;
          facility_type: string | null;
          account_id: string | null;
        }>;
        const acctIds = uniq(facs.map((f) => f.account_id));
        const acctsRes = acctIds.length
          ? await supabase.from('accounts').select('account_id, account_name').in('account_id', acctIds)
          : { data: [], error: null };
        if (acctsRes.error) throw acctsRes.error;
        const accts = (acctsRes.data ?? []) as Array<{ account_id: string; account_name: string | null }>;

        const facById = new Map(facs.map((f) => [f.facility_id, f]));
        const acctById = new Map(accts.map((a) => [a.account_id, a]));
        const withContract = new Set(
          ((contractsRes.data ?? []) as Array<{ facility_id: string | null }>)
            .map((c) => c.facility_id)
            .filter((x): x is string => !!x),
        );

        // facility_id -> dept -> {lines, hours}; plus week bounds.
        const grouped = new Map<
          string,
          { depts: Map<string, QueueDept>; minW: string | null; maxW: string | null }
        >();
        for (const r of rows) {
          if (!r.facility_id || !r.dept) continue;
          let g = grouped.get(r.facility_id);
          if (!g) {
            g = { depts: new Map(), minW: null, maxW: null };
            grouped.set(r.facility_id, g);
          }
          let d = g.depts.get(r.dept);
          if (!d) {
            d = { dept: r.dept, lines: 0, hours: 0 };
            g.depts.set(r.dept, d);
          }
          d.lines += 1;
          d.hours += Number(r.hours ?? 0);
          if (r.week_ending) {
            if (!g.minW || r.week_ending < g.minW) g.minW = r.week_ending;
            if (!g.maxW || r.week_ending > g.maxW) g.maxW = r.week_ending;
          }
        }

        const out: QueueFacility[] = [];
        for (const [facilityId, g] of grouped) {
          const fac = facById.get(facilityId);
          const depts = Array.from(g.depts.values()).sort((a, b) => a.dept.localeCompare(b.dept));
          out.push({
            facility_id: facilityId,
            facility_name: fac?.facility_name ?? facilityId,
            facility_type: fac?.facility_type ?? null,
            account_id: fac?.account_id ?? null,
            account_name: fac?.account_id ? acctById.get(fac.account_id)?.account_name ?? null : null,
            depts,
            total_lines: depts.reduce((s, d) => s + d.lines, 0),
            total_hours: depts.reduce((s, d) => s + d.hours, 0),
            earliest_week: g.minW,
            latest_week: g.maxW,
            has_active_contract: withContract.has(facilityId),
          });
        }
        out.sort((a, b) => b.total_hours - a.total_hours);
        if (!cancelled) setData(out);
      } catch (err) {
        if (!cancelled) {
          console.error('[invoicing] queue load failed', err);
          setError(errorMessage(err, 'Failed to load the invoicing queue.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return useMemo(() => ({ data, isLoading, error, refetch }), [data, isLoading, error, refetch]);
}

// ---------------------------------------------------------------------------
// Ready to invoice (AFB-0035) — derived readiness from v_ready_to_invoice.
// Active + started (effective_start <= today) + not 'No' override + no invoice
// covering the current month. Surfaces the real monthly backlog that the
// hours-line queue never could. View is security_invoker; finance/exec/su see
// all rows (the screen gates this list to those roles).
// ---------------------------------------------------------------------------

export interface ReadyToInvoiceResult {
  data: ReadyToInvoiceRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useReadyToInvoice(): ReadyToInvoiceResult {
  const [data, setData] = useState<ReadyToInvoiceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: rows, error: e } = await supabase
          .from('v_ready_to_invoice')
          .select('*')
          .order('monthly_rate', { ascending: false, nullsFirst: false })
          .order('facility_name', { ascending: true });
        if (e) throw e;
        if (!cancelled) setData((rows ?? []) as unknown as ReadyToInvoiceRow[]);
      } catch (err) {
        if (!cancelled) {
          console.error('[invoicing] ready-to-invoice load failed', err);
          setError(errorMessage(err, 'Failed to load the ready-to-invoice list.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return useMemo(() => ({ data, isLoading, error, refetch }), [data, isLoading, error, refetch]);
}

// ---------------------------------------------------------------------------
// Invoice list (RLS scopes rows: finance/exec/su all, AM own, hr hourly).
// ---------------------------------------------------------------------------

const INVOICE_COLS =
  'invoice_id, invoice_number, account_id, facility_id, billing_period_start, billing_period_end, billing_model, monthly_rate_amount, total_hours, hourly_rate, hours_subtotal, project_subtotal, adjustments, subtotal, total_amount, status, due_date, sent_at, paid_at, paid_amount, notes, created_at, updated_at';

async function attachNames(rows: InvoiceRow[]): Promise<InvoiceRow[]> {
  const facIds = uniq(rows.map((r) => r.facility_id));
  const acctIds = uniq(rows.map((r) => r.account_id));
  const [facsRes, acctsRes] = await Promise.all([
    facIds.length
      ? supabase.from('facilities').select('facility_id, facility_name, facility_type').in('facility_id', facIds)
      : Promise.resolve({ data: [], error: null }),
    acctIds.length
      ? supabase.from('accounts').select('account_id, account_name').in('account_id', acctIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  const facById = new Map(
    ((facsRes.data ?? []) as Array<{ facility_id: string; facility_name: string | null; facility_type: string | null }>).map(
      (f) => [f.facility_id, f],
    ),
  );
  const acctById = new Map(
    ((acctsRes.data ?? []) as Array<{ account_id: string; account_name: string | null }>).map((a) => [a.account_id, a]),
  );
  return rows.map((r) => ({
    ...r,
    facilities: r.facility_id
      ? { facility_name: facById.get(r.facility_id)?.facility_name ?? null, facility_type: facById.get(r.facility_id)?.facility_type ?? null }
      : null,
    accounts: { account_name: acctById.get(r.account_id)?.account_name ?? null },
  }));
}

export function useInvoices() {
  const [data, setData] = useState<InvoiceRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: inv, error: e } = await supabase
          .from('invoices')
          .select(INVOICE_COLS)
          .order('created_at', { ascending: false });
        if (e) throw e;
        const withNames = await attachNames((inv ?? []) as unknown as InvoiceRow[]);
        if (!cancelled) setData(withNames);
      } catch (err) {
        if (!cancelled) {
          console.error('[invoicing] invoices load failed', err);
          setError(errorMessage(err, 'Failed to load invoices.'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return useMemo(() => ({ data, isLoading, error, refetch }), [data, isLoading, error, refetch]);
}

export function useInvoice(invoiceId: string | null) {
  const [data, setData] = useState<InvoiceRow | null>(null);
  const [lineItems, setLineItems] = useState<InvoiceLineItemRow[]>([]);
  // The projects this invoice's lines bill, by id — the Project column's names
  // (item #542 part 2). Same fetch as the Change project picker.
  const [projects, setProjects] = useState<Record<string, ProjectRow>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!invoiceId) {
      setData(null);
      setLineItems([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: inv, error: e } = await supabase
          .from('invoices')
          .select(INVOICE_COLS)
          .eq('invoice_id', invoiceId)
          .maybeSingle();
        if (e) throw e;
        const [withNames] = await attachNames(inv ? [inv as unknown as InvoiceRow] : []);
        const { data: items, error: e2 } = await supabase
          .from('invoice_line_items')
          .select('*')
          .eq('invoice_id', invoiceId)
          .order('created_at', { ascending: true });
        if (e2) throw e2;
        const rows = (items ?? []) as unknown as InvoiceLineItemRow[];
        const projectIds = [...new Set(rows.map((r) => r.source_project_id).filter((v): v is string => Boolean(v)))];
        const projectRows = await fetchProjectRows({ projectIds });
        if (!cancelled) {
          setData(withNames ?? null);
          setLineItems(rows);
          setProjects(Object.fromEntries(projectRows.map((p) => [p.project_id, p])));
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Failed to load the invoice.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { data, lineItems, projects, isLoading, error, refetch };
}

// ---------------------------------------------------------------------------
// Facility contract rates — the approved Active-contract line items the user
// maps each dept to at draft time. Rates can ONLY come from here (governed).
// ---------------------------------------------------------------------------

export interface FacilityContractRates {
  contractId: string | null;
  billingAccountId: string | null;
  hourlyLines: ContractRateLine[];
  monthlyLines: ContractRateLine[];
}

export function useFacilityContractRates(facilityId: string | null) {
  const [data, setData] = useState<FacilityContractRates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!facilityId) {
      setData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: contract, error: e } = await supabase
          .from('contracts')
          .select('contract_id, billing_account_id')
          .eq('facility_id', facilityId)
          .eq('status', 'Active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (e) throw e;
        if (!contract) {
          if (!cancelled) setData({ contractId: null, billingAccountId: null, hourlyLines: [], monthlyLines: [] });
          return;
        }
        const c = contract as { contract_id: string; billing_account_id: string | null };
        const { data: items, error: e2 } = await supabase
          .from('contract_line_items')
          .select('line_id, contract_id, description, line_type, unit_rate, unit_label')
          .eq('contract_id', c.contract_id)
          .order('created_at', { ascending: true });
        if (e2) throw e2;
        const lines = (items ?? []) as unknown as ContractRateLine[];
        if (!cancelled) {
          setData({
            contractId: c.contract_id,
            billingAccountId: c.billing_account_id,
            hourlyLines: lines.filter((l) => l.line_type === 'Hourly_Position'),
            monthlyLines: lines.filter((l) => l.line_type === 'Recurring_Monthly'),
          });
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Failed to load contract rates.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  return { data, isLoading, error };
}

// ---------------------------------------------------------------------------
// Mutations — draft creation (multi-rate RPC) + status transitions.
// ---------------------------------------------------------------------------

export interface CreateDraftInput {
  facilityId: string;
  periodStart: string;
  periodEnd: string;
  deptRateMap: DeptRateMapEntry[];
  monthlyLineIds: string[];
  notes: string | null;
}

export async function createInvoiceDraftV2(input: CreateDraftInput): Promise<string> {
  const { data, error } = await supabase.rpc('create_invoice_draft_v2', {
    p_facility_id: input.facilityId,
    p_billing_period_start: input.periodStart,
    p_billing_period_end: input.periodEnd,
    p_dept_rate_map: input.deptRateMap,
    p_monthly_line_ids: input.monthlyLineIds,
    p_notes: input.notes,
  });
  if (error) throw error;
  return data as unknown as string;
}

// Monthly-rate draft path (2026-06-30). Facilities that run on a plain
// facilities.monthly_rate (no contract line items) can't use v2 — this routes
// them to create_monthly_invoice_draft. Draft-only; no send / no QBO.
export async function createMonthlyInvoiceDraft(input: {
  facilityId: string;
  periodStart: string;
  periodEnd: string;
  notes: string | null;
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_monthly_invoice_draft', {
    p_facility_id: input.facilityId,
    p_billing_period_start: input.periodStart,
    p_billing_period_end: input.periodEnd,
    p_notes: input.notes,
  });
  if (error) throw error;
  return data as unknown as string;
}

// Prior calendar month as { start, end } in YYYY-MM-DD — the default billing
// period for monthly draft generation.
export function priorCalendarMonth(now: Date = new Date()): { start: string; end: string } {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based current month
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(new Date(y, m - 1, 1)), end: fmt(new Date(y, m, 0)) };
}

export async function validateInvoiceHours(invoiceId: string, notes: string | null): Promise<void> {
  const { error } = await supabase.rpc('validate_invoice_hours', { p_invoice_id: invoiceId, p_notes: notes });
  if (error) throw error;
}

export async function approveInvoice(invoiceId: string, notes: string | null): Promise<void> {
  const { error } = await supabase.rpc('approve_invoice', { p_invoice_id: invoiceId, p_notes: notes });
  if (error) throw error;
}

export async function markInvoiceSent(invoiceId: string, dueDate: string | null): Promise<void> {
  // Status-only (Phase 1.5): no QBO write, so p_qbo_invoice_id stays null.
  const { error } = await supabase.rpc('mark_invoice_sent', {
    p_invoice_id: invoiceId,
    p_qbo_invoice_id: null,
    p_due_date: dueDate,
  });
  if (error) throw error;
}

export async function markInvoicePaid(invoiceId: string, paidAmount: number): Promise<void> {
  const { error } = await supabase.rpc('mark_invoice_paid', { p_invoice_id: invoiceId, p_paid_amount: paidAmount });
  if (error) throw error;
}

export async function voidInvoice(invoiceId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('void_invoice', { p_invoice_id: invoiceId, p_reason: reason });
  if (error) throw error;
}

/**
 * Change project on an invoice line (item #542 part 2, TCSD-0490). Moves every
 * line the old project has on this invoice to the new project, rewritten from
 * it, and moves the invoice mark from the old project to the new one — all in
 * change_invoice_line_project, one transaction. Finance only; unsent bills only;
 * the new project must be Ready to Invoice at the same building and on no bill.
 */
export async function changeInvoiceLineProject(lineId: string, projectId: string): Promise<void> {
  const { error } = await supabase.rpc('change_invoice_line_project', {
    p_line_id: lineId,
    p_project_id: projectId,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Dashboard summary — unbilled (facilities + hours; $ unknown pre-rate) and
// draft-invoice ($ booked). Reads are RLS-scoped to the viewer.
// ---------------------------------------------------------------------------

export interface InvoicingSummary {
  unbilledFacilities: number;
  unbilledHours: number;
  draftCount: number;
  draftValue: number;
}

// Every status before the bill is sent. Also the statuses Change project is
// allowed on (Ben, 2026-10-19, Decision 1) — change_invoice_line_project
// refuses the same set server-side.
export const DRAFT_STATUSES = ['Draft', 'Pending_Hours_Validation', 'Pending_Finance_Review', 'Approved'];

export function useInvoicingSummary() {
  const [data, setData] = useState<InvoicingSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const [linesRes, invRes] = await Promise.all([
          supabase
            .from('client_billing_lines')
            .select('facility_id, hours')
            .is('invoice_id', null)
            .not('hours', 'is', null),
          supabase.from('invoices').select('status, total_amount').in('status', DRAFT_STATUSES),
        ]);
        if (linesRes.error) throw linesRes.error;
        if (invRes.error) throw invRes.error;
        const lines = (linesRes.data ?? []) as Array<{ facility_id: string | null; hours: number | null }>;
        const inv = (invRes.data ?? []) as Array<{ status: string; total_amount: number | null }>;
        const summary: InvoicingSummary = {
          unbilledFacilities: uniq(lines.map((l) => l.facility_id)).length,
          unbilledHours: lines.reduce((s, l) => s + Number(l.hours ?? 0), 0),
          draftCount: inv.length,
          draftValue: inv.reduce((s, i) => s + Number(i.total_amount ?? 0), 0),
        };
        if (mounted.current) setData(summary);
      } catch (err) {
        if (mounted.current) setError(errorMessage(err, 'Failed to load invoicing summary.'));
      } finally {
        if (mounted.current) setIsLoading(false);
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, []);

  return { data, isLoading, error };
}
